import React, { useEffect, useState } from 'react';
import { Film, Download, Check } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { formatMediaDuration, getResourceThumbnailUrl, isVideoResource } from './resourceViewUtils';
import StudioArtwork from '../ui/StudioArtwork';
import './ResourceCard.css';

/**
 * 资源卡片组件
 */
const ResourceCard = ({ resource, isSelected, onToggleSelect, onPreview }) => {
  const { getTheme } = useTheme();
  const theme = getTheme();
  const [downloading, setDownloading] = useState(false);
  const [imgError, setImgError] = useState(false);

  const isVideo = isVideoResource(resource);
  const thumbnailUrl = getResourceThumbnailUrl(resource);
  const durationLabel = formatMediaDuration(resource.duration);
  const resourceName = resource.name?.trim() || (isVideo ? '未命名视频' : '未命名图片');
  const sourceLabel = resource.sourceLabel || resource.source || resource.typeLabel || '';

  useEffect(() => {
    setImgError(false);
  }, [thumbnailUrl]);

  // 获取实际下载地址（视频用 resource.url，图片用 resource.url 或 thumbnailUrl）
  const downloadUrl = isVideo ? resource.url : (resource.url || resource.thumbnailUrl);

  const handleDownload = async (e) => {
    e.stopPropagation();
    if (!downloadUrl) {
      console.error('下载地址不存在');
      return;
    }
    
    setDownloading(true);
    try {
      const ext = isVideo ? '.mp4' : '.png';
      const filename = (resource.name || `resource_${resource.id}`) + ext;
      
      // 直接通过 URL 下载资源
      const response = await fetch(downloadUrl);
      if (!response.ok) throw new Error('下载失败');
      
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('下载失败:', err);
      // 如果 fetch 失败，尝试直接用链接下载
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = (resource.name || `resource_${resource.id}`) + (isVideo ? '.mp4' : '.png');
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } finally {
      setDownloading(false);
    }
  };

  const handleCheckbox = (e) => {
    e.stopPropagation();
    onToggleSelect(resource.id);
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return `${d.getMonth() + 1}/${d.getDate()} ${d.getHours()}:${String(d.getMinutes()).padStart(2, '0')}`;
  };

  return (
    <div
      className="resource-card relative rounded-lg overflow-hidden aspect-square"
      data-selected={isSelected ? 'true' : 'false'}
      style={{
        border: '2px solid transparent',
        backgroundColor: isSelected
          ? 'var(--studio-selected-fill, var(--color-bg-overlay))'
          : 'var(--studio-surface, var(--color-bg-overlay))'
      }}
    >
      <button
        type="button"
        className="resource-card__preview"
        aria-label={`预览${isVideo ? '视频' : '图片'}：${resourceName}`}
        title={resourceName}
        onClick={() => onPreview && onPreview(resource)}
      />
      {/* 缩略图 - 视频用缩略图或占位图（不再用video标签，避免大量并发加载） */}
      {isVideo ? (
        thumbnailUrl && !imgError ? (
          <img
            src={thumbnailUrl}
            alt={resourceName}
            className="resource-card__media w-full h-full object-contain"
            loading="lazy"
            decoding="async"
            draggable={false}
            onError={() => setImgError(true)}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center"
            style={{ backgroundColor: 'var(--color-bg-primary)' }}>
            <StudioArtwork variant="media" className="resource-card__artwork" />
          </div>
        )
      ) : thumbnailUrl && !imgError ? (
        <img
          src={thumbnailUrl}
          alt={resourceName}
          className="resource-card__media w-full h-full object-contain"
          loading="lazy"
          decoding="async"
          draggable={false}
          onError={() => setImgError(true)}
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center"
          style={{ color: 'var(--color-text-muted)' }}>
          <StudioArtwork variant="media" className="resource-card__artwork" />
        </div>
      )}

      {/* 视频标识 */}
      {isVideo && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="resource-card__video-badge w-10 h-10 rounded-full flex items-center justify-center"
            style={{ backgroundColor: 'var(--overlay-medium)' }}>
            <Film size={16} className="text-white" />
          </div>
        </div>
      )}

      {durationLabel && (
        <div
          className="resource-card__duration absolute bottom-12 left-1.5 rounded px-1.5 py-0.5 text-2xs font-mono text-white pointer-events-none"
          style={{ backgroundColor: 'var(--overlay-heavy)' }}
        >
          {durationLabel}
        </div>
      )}

      {/* 多选框 - 左上角 */}
      <button
        type="button"
        className="resource-card__action resource-card__select absolute top-1.5 left-1.5 z-10 rounded border-2 flex items-center justify-center"
        onClick={handleCheckbox}
        aria-label={`${isSelected ? '取消选择' : '选择'}：${resourceName}`}
        aria-pressed={Boolean(isSelected)}
        title={isSelected ? '取消选择' : '选择资源'}
        style={{
          backgroundColor: isSelected ? theme.colors.accentPrimary : 'var(--studio-surface, var(--color-bg-secondary))',
          borderColor: isSelected ? theme.colors.accentPrimary : 'var(--studio-line-strong, var(--color-border-hover))'
        }}
      >
        {isSelected && <Check size={14} className="text-white" />}
      </button>

      {/* 下载按钮 - 右上角 */}
      <button
        type="button"
        className="resource-card__action resource-card__download absolute top-1.5 right-1.5 z-10 rounded-full flex items-center justify-center"
        style={{ backgroundColor: 'var(--studio-surface, var(--color-bg-secondary))' }}
        onClick={handleDownload}
        disabled={downloading}
        aria-label={downloading ? '正在下载' : '下载资源'}
        title={downloading ? '正在下载' : '下载'}
      >
        <Download size={14} className="text-white" />
      </button>

      {/* 名称与来源 - 底部信息层 */}
      <div className="resource-card__metadata absolute bottom-0 left-0 right-0 px-2 py-1.5 pointer-events-none">
        <div className="resource-card__name truncate text-xs font-medium text-white" title={resourceName}>
          {resourceName}
        </div>
        <div className="mt-0.5 flex min-w-0 items-center justify-between gap-2">
          <span className="resource-card__source truncate text-2xs text-white/70">{sourceLabel}</span>
          {resource.createdAt && (
            <span className="resource-card__time shrink-0 text-2xs text-white/55">
              {formatTime(resource.createdAt)}
            </span>
          )}
        </div>
      </div>

      {/* 分镜号标签 */}
      {resource.shotNumber && (
        <div className="absolute top-1.5 left-10 px-1.5 py-0.5 rounded text-2xs font-mono pointer-events-none"
          style={{ backgroundColor: 'var(--overlay-medium)', color: 'white' }}>
          #{String(resource.shotNumber).padStart(3, '0')}
        </div>
      )}
    </div>
  );
};

export default ResourceCard;
